// Shared <Chart> component — single SVG renderer for every aggregation
// surface in the dashboard (Explorer AggregateView + AnaMoni chat bubble).
//
// Inputs are the AggregationResult contract from types/chart.ts; the
// caller decides how rows were computed (page buffer / full set / chat
// turn). All animation is CSS-only on `d` / `opacity` — no library, no
// virtualisation, no measurement loop.
//
// Why no recharts? Bundle hygiene (Senior Dev's precedent), and SVG
// export needs raw DOM access we'd lose behind a third-party wrapper.
import { useId, useMemo } from "react";
import type {
  AggregationResult,
  AggregationRow,
} from "../../types/chart";
import type { ChartCustomization } from "./customization";
import { colorAt, getPalette } from "./palettes";
import { arcPath } from "./arcMath";

export interface ChartProps {
  result: AggregationResult;
  onSliceClick?: (row: AggregationRow) => void;
  customization: ChartCustomization;
  height?: number;
  /** Stable id for export filenames + DOM ids. */
  exportId?: string;
}

interface PreparedRow extends AggregationRow {
  color: string;
}

function isClickableRow(
  row: AggregationRow,
  onSliceClick: ((row: AggregationRow) => void) | undefined,
): boolean {
  return Boolean(onSliceClick)
    && row.group !== null
    && !row.rollup
    && !row.suppressed
    && !row.is_empty;
}

function sortRows(rows: AggregationRow[], mode: ChartCustomization["sortMode"]): AggregationRow[] {
  const copy = [...rows];
  if (mode === "alpha") {
    copy.sort((a, b) => rowLabel(a).localeCompare(rowLabel(b)));
  } else if (mode === "asc") {
    copy.sort((a, b) => a.count - b.count || rowLabel(a).localeCompare(rowLabel(b)));
  } else {
    copy.sort((a, b) => b.count - a.count || rowLabel(a).localeCompare(rowLabel(b)));
  }
  return copy;
}

function applyTopN(rows: AggregationRow[], topN: number, total: number): AggregationRow[] {
  if (rows.length <= topN) return rows;
  const head = rows.slice(0, topN);
  const tail = rows.slice(topN);
  const tailCount = tail.reduce((n, r) => n + r.count, 0);
  if (tailCount <= 0) return head;
  head.push({
    group: `Other (${tail.length} ${tail.length === 1 ? "group" : "groups"})`,
    count: tailCount,
    pct: total === 0 ? 0 : tailCount / total,
    rollup: true,
    rollup_groups: tail.map((r) => rowLabel(r)),
  });
  return head;
}

function prepareRows(result: AggregationResult, customization: ChartCustomization): PreparedRow[] {
  const palette = getPalette(customization.palette);
  // Don't re-roll the server's "Other" row — the /aggregate endpoint already
  // returns one (flagged rollup) when top_n was requested, and rolling up again
  // would create a duplicate/incorrect "Other". Detect it by the `rollup` flag,
  // NOT the label, so a real category literally named "Other" is unaffected.
  // Client-side top_n only kicks in for callers (chat) that don't pass top_n.
  const protectedRows = result.rows.filter((row) => row.suppressed || row.is_empty);
  const publicRows = result.rows.filter((row) => !row.suppressed && !row.is_empty);
  const sorted = sortRows(publicRows, customization.sortMode);
  const serverAlreadyCapped = publicRows.some((row) => row.rollup);
  const capped = serverAlreadyCapped
    ? sorted
    : applyTopN(sorted, customization.topN, result.membership_count);
  return [...capped, ...protectedRows]
    .map((row, i) => ({ ...row, color: colorAt(palette, i) }));
}

export function Chart({
  result,
  onSliceClick,
  customization,
  height,
  exportId,
}: ChartProps) {
  const reactId = useId();
  const id = exportId ?? `chart-${reactId.replace(/:/g, "")}`;
  const rows = useMemo(() => prepareRows(result, customization), [result, customization]);

  if (rows.length === 0) {
    return (
      <div className="chart chart--empty" role="status">
        No data to display for this breakdown.
      </div>
    );
  }

  const isTable = customization.chartType === "table";
  return (
    <div className="chart" data-chart-id={id}>
      {!isTable && (
        <ChartSvg
          rows={rows}
          total={result.total}
          customization={customization}
          height={height}
          onSliceClick={onSliceClick}
          id={id}
        />
      )}
      {(customization.showLegend || isTable) && (
        <ChartLegend
          rows={rows}
          total={result.total}
          customization={customization}
          onSliceClick={onSliceClick}
          asTable={isTable}
        />
      )}
    </div>
  );
}

// ─── SVG dispatcher ─────────────────────────────────────────────────────

interface SvgProps {
  rows: PreparedRow[];
  total: number;
  customization: ChartCustomization;
  height?: number;
  onSliceClick?: (row: AggregationRow) => void;
  id: string;
}

function ChartSvg({ rows, total, customization, height, onSliceClick, id }: SvgProps) {
  const type = customization.chartType;
  if (type === "pie" || type === "donut") {
    return (
      <PieDonut
        rows={rows}
        total={total}
        innerRatio={type === "donut" ? 0.6 : 0}
        height={height ?? 340}
        onSliceClick={onSliceClick}
        labelMode={customization.labelMode}
        id={id}
      />
    );
  }
  // bar, horizontal_bar, stacked_bar (single-series → renders as plain bar)
  const horizontal = type === "horizontal_bar";
  return (
    <BarChart
      rows={rows}
      horizontal={horizontal}
      height={height ?? 320}
      axisLabelAngle={horizontal ? 0 : customization.axisLabelAngle}
      onSliceClick={onSliceClick}
      labelMode={customization.labelMode}
      id={id}
    />
  );
}

// ─── Pie / donut ────────────────────────────────────────────────────────

interface PieDonutProps {
  rows: PreparedRow[];
  total: number;
  innerRatio: number;
  height: number;
  onSliceClick?: (row: AggregationRow) => void;
  labelMode: ChartCustomization["labelMode"];
  id: string;
}

function PieDonut({
  rows, total, innerRatio, height, onSliceClick, labelMode, id,
}: PieDonutProps) {
  const size = height;
  const outerR = size * 0.42;
  const innerR = outerR * innerRatio;
  const dims = { cx: size / 2, cy: size / 2, outerR, innerR };
  let cursor = 0;
  const totalForAngles = rows.reduce((n, r) => n + r.count, 0) || 1;
  return (
    <svg
      id={id}
      className="chart__svg chart__svg--pie"
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${innerRatio > 0 ? "Donut" : "Pie"} chart — ${total} total across ${rows.length} groups`}
      preserveAspectRatio="xMidYMid meet"
    >
      {rows.map((row) => {
        const startAngle = (cursor / totalForAngles) * Math.PI * 2;
        cursor += row.count;
        const endAngle = (cursor / totalForAngles) * Math.PI * 2;
        const d = arcPath(dims, startAngle, endAngle);
        const clickable = isClickableRow(row, onSliceClick);
        return (
          <path
            key={rowLabel(row)}
            d={d}
            fill={row.color}
            stroke="var(--color-neutral-0)"
            strokeWidth={1}
            style={{ cursor: clickable ? "pointer" : "default", transition: "opacity var(--transition-base, 200ms)" }}
            onClick={clickable ? () => onSliceClick?.(row) : undefined}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={(e) => {
              if (!clickable) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSliceClick?.(row);
              }
            }}
          >
            <title>{titleFor(row)}</title>
          </path>
        );
      })}
      {innerRatio > 0 && (
        <text
          x={dims.cx}
          y={dims.cy}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={size * 0.12}
          fontWeight={600}
          fill="var(--color-neutral-900)"
        >
          {total}
        </text>
      )}
      {labelMode !== "hide" && (
        <PieLabels rows={rows} dims={dims} totalForAngles={totalForAngles} labelMode={labelMode} />
      )}
    </svg>
  );
}

function PieLabels({
  rows, dims, totalForAngles, labelMode,
}: {
  rows: PreparedRow[];
  dims: { cx: number; cy: number; outerR: number; innerR: number };
  totalForAngles: number;
  labelMode: ChartCustomization["labelMode"];
}) {
  let cursor = 0;
  const labelR = (dims.outerR + dims.innerR) / 2 || dims.outerR * 0.7;
  return (
    <g pointerEvents="none">
      {rows.map((row) => {
        const startAngle = (cursor / totalForAngles) * Math.PI * 2;
        cursor += row.count;
        const endAngle = (cursor / totalForAngles) * Math.PI * 2;
        const mid = (startAngle + endAngle) / 2;
        // Drop labels under 4% — they overlap and add visual noise.
        if (row.pct < 0.04) return null;
        const x = dims.cx + Math.cos(mid - Math.PI / 2) * labelR;
        const y = dims.cy + Math.sin(mid - Math.PI / 2) * labelR;
        return (
          <text
            key={rowLabel(row)}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={11}
            fontWeight={600}
            fill="#fff"
            style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.25)", strokeWidth: 2 }}
          >
            {formatLabel(row, labelMode)}
          </text>
        );
      })}
    </g>
  );
}

// ─── Bar / horizontal bar ───────────────────────────────────────────────

interface BarProps {
  rows: PreparedRow[];
  horizontal: boolean;
  height: number;
  axisLabelAngle: ChartCustomization["axisLabelAngle"];
  onSliceClick?: (row: AggregationRow) => void;
  labelMode: ChartCustomization["labelMode"];
  id: string;
}

function BarChart({ rows, horizontal, height, axisLabelAngle, onSliceClick, labelMode, id }: BarProps) {
  const width = 600;
  const angle = horizontal ? 0 : axisLabelAngle;
  // Rotated labels can show more characters before truncation.
  const catTrunc = angle === 0 ? 12 : 18;
  const longest = rows.reduce((m, r) => Math.max(m, truncateLabel(rowLabel(r), catTrunc).length), 1);
  // Vertical bars need extra room below the plot when labels are rotated.
  const bottomPad =
    angle === 0 ? 40 : Math.min(140, 24 + Math.round(longest * (angle === 90 ? 6.2 : 4.6)));
  const padding = { top: 16, right: 24, bottom: horizontal ? 40 : bottomPad, left: horizontal ? 120 : 40 };
  // Grow the canvas downward so rotated labels don't steal bar height.
  const svgHeight = height + Math.max(0, padding.bottom - 40);
  const maxCount = Math.max(...rows.map((r) => r.count), 1);
  const plotW = width - padding.left - padding.right;
  const plotH = svgHeight - padding.top - padding.bottom;
  return (
    <svg
      id={id}
      className="chart__svg chart__svg--bar"
      viewBox={`0 0 ${width} ${svgHeight}`}
      role="img"
      aria-label={`${horizontal ? "Horizontal bar" : "Bar"} chart — ${rows.length} groups`}
      preserveAspectRatio="xMidYMid meet"
    >
      <Axis
        rows={rows}
        horizontal={horizontal}
        plotW={plotW}
        plotH={plotH}
        padding={padding}
        maxCount={maxCount}
      />
      {rows.map((row, i) =>
        horizontal
          ? renderHBar(row, i, rows.length, plotW, plotH, padding, maxCount, onSliceClick, labelMode)
          : renderVBar(row, i, rows.length, plotW, plotH, padding, maxCount, onSliceClick, labelMode, angle, catTrunc),
      )}
    </svg>
  );
}

function renderVBar(
  row: PreparedRow,
  i: number,
  n: number,
  plotW: number,
  plotH: number,
  padding: { top: number; left: number },
  maxCount: number,
  onSliceClick: ((row: AggregationRow) => void) | undefined,
  labelMode: ChartCustomization["labelMode"],
  angle: ChartCustomization["axisLabelAngle"],
  catTrunc: number,
) {
  const slot = plotW / n;
  const bw = Math.max(2, slot * 0.7);
  const x = padding.left + slot * i + (slot - bw) / 2;
  const bh = (row.count / maxCount) * plotH;
  const y = padding.top + plotH - bh;
  const clickable = isClickableRow(row, onSliceClick);
  const cx = x + bw / 2;
  const labelY = padding.top + plotH + (angle === 0 ? 14 : 12);
  const labelAnchor = angle === 0 ? "middle" : "end";
  const labelTransform = angle === 0 ? undefined : `rotate(-${angle} ${cx} ${labelY})`;
  return (
    <g key={rowLabel(row)}>
      <rect
        x={x}
        y={y}
        width={bw}
        height={bh}
        fill={row.color}
        style={{ cursor: clickable ? "pointer" : "default", transition: "opacity var(--transition-base, 200ms)" }}
        onClick={clickable ? () => onSliceClick?.(row) : undefined}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
      >
        <title>{titleFor(row)}</title>
      </rect>
      {labelMode !== "hide" && (
        <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize={11} fill="var(--color-neutral-800, #3D3D3D)">
          {formatLabel(row, labelMode)}
        </text>
      )}
      <text
        x={cx}
        y={labelY}
        textAnchor={labelAnchor}
        transform={labelTransform}
        fontSize={10}
        fill="var(--color-neutral-600, #777)"
      >
        {truncateLabel(rowLabel(row), catTrunc)}
      </text>
    </g>
  );
}

function renderHBar(
  row: PreparedRow,
  i: number,
  n: number,
  plotW: number,
  plotH: number,
  padding: { top: number; left: number },
  maxCount: number,
  onSliceClick: ((row: AggregationRow) => void) | undefined,
  labelMode: ChartCustomization["labelMode"],
) {
  const slot = plotH / n;
  const bh = Math.max(2, slot * 0.7);
  const y = padding.top + slot * i + (slot - bh) / 2;
  const bw = (row.count / maxCount) * plotW;
  const x = padding.left;
  const clickable = isClickableRow(row, onSliceClick);
  return (
    <g key={rowLabel(row)}>
      <rect
        x={x}
        y={y}
        width={bw}
        height={bh}
        fill={row.color}
        style={{ cursor: clickable ? "pointer" : "default", transition: "opacity var(--transition-base, 200ms)" }}
        onClick={clickable ? () => onSliceClick?.(row) : undefined}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
      >
        <title>{titleFor(row)}</title>
      </rect>
      <text x={x - 6} y={y + bh / 2} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--color-neutral-800, #3D3D3D)">
        {truncateLabel(rowLabel(row), 18)}
      </text>
      {labelMode !== "hide" && (
        <text x={x + bw + 4} y={y + bh / 2} dominantBaseline="middle" fontSize={11} fill="var(--color-neutral-800, #3D3D3D)">
          {formatLabel(row, labelMode)}
        </text>
      )}
    </g>
  );
}

// ─── Axis (used by both bar variants) ───────────────────────────────────

interface AxisProps {
  rows: PreparedRow[];
  horizontal: boolean;
  plotW: number;
  plotH: number;
  padding: { top: number; right: number; bottom: number; left: number };
  maxCount: number;
}

function Axis({ horizontal, plotW, plotH, padding, maxCount }: AxisProps) {
  // 4 gridlines including 0 and max.
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(t * maxCount));
  return (
    <g className="chart__axis" stroke="var(--color-neutral-200, #E2E2E2)" strokeWidth={1}>
      {horizontal ? (
        ticks.map((t, i) => {
          const x = padding.left + (t / maxCount) * plotW;
          return (
            <g key={i}>
              <line x1={x} y1={padding.top} x2={x} y2={padding.top + plotH} />
              <text x={x} y={padding.top + plotH + 14} textAnchor="middle" fontSize={10} fill="var(--color-neutral-600, #777)" stroke="none">{t}</text>
            </g>
          );
        })
      ) : (
        ticks.map((t, i) => {
          const y = padding.top + plotH - (t / maxCount) * plotH;
          return (
            <g key={i}>
              <line x1={padding.left} y1={y} x2={padding.left + plotW} y2={y} />
              <text x={padding.left - 6} y={y} textAnchor="end" dominantBaseline="middle" fontSize={10} fill="var(--color-neutral-600, #777)" stroke="none">{t}</text>
            </g>
          );
        })
      )}
    </g>
  );
}

// ─── Legend / Table ─────────────────────────────────────────────────────

interface LegendProps {
  rows: PreparedRow[];
  total: number;
  customization: ChartCustomization;
  onSliceClick?: (row: AggregationRow) => void;
  asTable: boolean;
}

function ChartLegend({ rows, total, onSliceClick, asTable }: LegendProps) {
  return (
    <table
      className={asTable ? "chart__table chart__table--full" : "chart__table chart__table--legend"}
      aria-label="Group counts"
    >
      <thead>
        <tr>
          <th>Group</th>
          <th className="num">Count</th>
          <th className="num">%</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const clickable = isClickableRow(row, onSliceClick);
          return (
            <tr
              key={rowLabel(row)}
              className={clickable ? "is-clickable" : undefined}
              onClick={clickable ? () => onSliceClick?.(row) : undefined}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={(e) => {
                if (!clickable) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSliceClick?.(row);
                }
              }}
              title={clickable ? `Filter by ${rowLabel(row)}` : (row.rollup ? titleFor(row) : undefined)}
            >
              <td>
                <span className="chart__swatch" style={{ background: row.color }} aria-hidden="true" />
                {rowLabel(row)}
              </td>
              <td className="num">{row.count}</td>
              <td className="num">{total === 0 ? "0.0%" : `${(row.pct * 100).toFixed(1)}%`}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

function formatLabel(row: AggregationRow, mode: ChartCustomization["labelMode"]): string {
  if (mode === "count") return String(row.count);
  if (mode === "percent") return `${(row.pct * 100).toFixed(0)}%`;
  if (mode === "both") return `${row.count} · ${(row.pct * 100).toFixed(0)}%`;
  return "";
}

function truncateLabel(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}

function rowLabel(row: AggregationRow): string {
  return row.label ?? row.group ?? "(unspecified)";
}

// Native SVG/HTML tooltip text. For a rollup bucket, list the folded groups so
// "Other (34 groups)" reveals exactly which countries it hides on hover.
function titleFor(row: AggregationRow): string {
  const base = `${rowLabel(row)}: ${row.count} (${(row.pct * 100).toFixed(1)}%)`;
  const groups = row.rollup_groups;
  if (row.rollup && groups && groups.length > 0) {
    const shown = groups.slice(0, 24).join(", ");
    const extra = groups.length > 24 ? `, +${groups.length - 24} more` : "";
    return `${base}\nIncludes: ${shown}${extra}`;
  }
  return base;
}
